/**
 * SkillsMatrix — F-SKILL-1 Packet B3
 *
 * AC-SKILL-14 (F): Assign/unassign skills to operators via matrix toggles.
 * AC-SKILL-25 (F): View-only users see no mutate controls.
 *
 * Shows a skill × operator grid. Each cell is a Switch toggle for admin users.
 * View-only users see read-only badges.
 *
 * Data sources:
 *   - useRorosSkillMatrix()  — SkillMatrixEntry[] (skills + assigned agents)
 *   - useRorosAgents()       — RorosAgentDefinitionDTO[] (all agent defs with actorId)
 *   - useRorosActors()       — RorosActorDTO[] (AI Operator actors for names)
 */

import React, { useMemo, useState } from 'react';
import { t } from '@/lib/i18n';
import { Switch } from '@/components/ui/Switch';
import { Badge } from '@/components/ui/Badge';
import { showToast } from '@/components/ui/Toast';
import { useRorosSkillMatrix, useRorosSkillAssign, useRorosSkillUnassign } from '@/hooks/roros/skills';
import { useRorosAgents } from '@/hooks/roros/agents';
import { useRorosActors } from '@/hooks/roros/actors';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface OperatorColumn {
  agentDefinitionId: string;
  actorName: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SkillsMatrix() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.ROROS_AGENT_SKILLS_MANAGE);

  const matrixQuery = useRorosSkillMatrix();
  const agentsQuery = useRorosAgents();
  const actorsQuery = useRorosActors({ type: 'ai_operator' });

  const assignMutation = useRorosSkillAssign();
  const unassignMutation = useRorosSkillUnassign();

  // Track pending toggles to show optimistic state without double-click issues
  const [pending, setPending] = useState<Set<string>>(new Set());

  // Build the operator columns: all agent definitions that belong to AI Operator actors
  const columns = useMemo<OperatorColumn[]>(() => {
    const agentDefs = agentsQuery.data ?? [];
    const actors = actorsQuery.data ?? [];
    const actorMap = new Map(actors.map((a) => [a.id, a.name]));

    return agentDefs
      .filter((def) => actorMap.has(def.actorId))
      .map((def) => ({
        agentDefinitionId: def.id,
        actorName: actorMap.get(def.actorId) ?? def.id.substring(0, 6),
      }));
  }, [agentsQuery.data, actorsQuery.data]);

  const skills = matrixQuery.data ?? [];

  // Build a lookup: skillId → Set<agentDefinitionId>
  const assignedMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const entry of skills) {
      map.set(entry.skillId, new Set(entry.assignedAgents.map((a) => a.agentDefinitionId)));
    }
    return map;
  }, [skills]);

  const isLoading =
    matrixQuery.isLoading || agentsQuery.isLoading || actorsQuery.isLoading;

  const isError = matrixQuery.isError || agentsQuery.isError || actorsQuery.isError;

  async function handleToggle(skillId: string, agentDefinitionId: string, currentlyAssigned: boolean) {
    const key = `${skillId}:${agentDefinitionId}`;
    if (pending.has(key)) return;

    setPending((prev) => new Set(prev).add(key));
    try {
      if (currentlyAssigned) {
        await unassignMutation.mutateAsync({ id: skillId, agentDefinitionId });
        showToast.success(t('Skill unassigned'));
      } else {
        await assignMutation.mutateAsync({ id: skillId, agentDefinitionId });
        showToast.success(t('Skill assigned'));
      }
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : t('Assignment failed'));
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }

  if (isLoading) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm" data-testid="skills-matrix-loading">
        {t('Loading…')}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-8 text-center text-destructive text-sm" data-testid="skills-matrix-error">
        {t('Failed to load matrix')}
      </div>
    );
  }

  if (skills.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm" data-testid="skills-matrix-empty">
        {t('No skills found')}
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm" data-testid="skills-matrix-no-operators">
        {t('No AI Operators configured yet. Create an AI Operator first.')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border" data-testid="skills-matrix-table">
      <table className="w-full text-sm" aria-label={t('Skill assignment matrix')}>
        <thead>
          <tr className="border-b border-border bg-muted/40">
            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 min-w-[200px]">
              {t('Skill')}
            </th>
            {columns.map((col) => (
              <th
                key={col.agentDefinitionId}
                className="text-center text-xs font-semibold text-muted-foreground px-3 py-2.5 min-w-[120px]"
                data-testid={`matrix-col-${col.agentDefinitionId}`}
              >
                {col.actorName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {skills.map((entry) => {
            const assignedSet = assignedMap.get(entry.skillId) ?? new Set();
            return (
              <tr
                key={entry.skillId}
                className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                data-testid={`matrix-row-${entry.skillId}`}
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-foreground">{entry.skillDisplayName}</div>
                  <div className="flex gap-1 mt-0.5">
                    {!entry.isActive && (
                      <Badge variant="secondary" className="text-xs">
                        {t('Inactive')}
                      </Badge>
                    )}
                    {entry.isSystem && (
                      <Badge variant="outline" className="text-xs">
                        {t('System')}
                      </Badge>
                    )}
                  </div>
                </td>
                {columns.map((col) => {
                  const assigned = assignedSet.has(col.agentDefinitionId);
                  const key = `${entry.skillId}:${col.agentDefinitionId}`;
                  const isPending = pending.has(key);

                  return (
                    <td
                      key={col.agentDefinitionId}
                      className="px-3 py-3 text-center"
                      data-testid={`matrix-cell-${entry.skillId}-${col.agentDefinitionId}`}
                    >
                      {canManage ? (
                        <Switch
                          checked={assigned}
                          disabled={isPending}
                          onChange={() => handleToggle(entry.skillId, col.agentDefinitionId, assigned)}
                          aria-label={`${t('Assign')} ${entry.skillDisplayName} ${t('to')} ${col.actorName}`}
                          data-testid={`matrix-toggle-${entry.skillId}-${col.agentDefinitionId}`}
                        />
                      ) : (
                        assigned ? (
                          <Badge
                            variant="success"
                            className="text-xs"
                            data-testid={`matrix-assigned-badge-${entry.skillId}-${col.agentDefinitionId}`}
                          >
                            {t('Assigned')}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
